import { translateBackend } from '@assets/i18n/config';
import {
  CustomFieldFormats,
  CustomFieldTypes,
  type CustomOption,
} from '@interfaces/customField';
import type { TranslatedString } from '@interfaces/shared';
import { Chip, FormControlLabel, ListItemText } from '@mui/material';
import type { CountryCodes } from '@onevesthq/ov-enums';
import { AccountAgreements } from '@ovComponents/2-component/accountAgreements/accountAgreements';
import { AddressField } from '@ovComponents/2-component/addressField/addressField';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { CheckboxField } from '@ovComponents/2-component/checkboxField/checkboxField';
import { DateField } from '@ovComponents/2-component/dateField/dateField';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { SwitchField } from '@ovComponents/3-pattern/switchField/switchField';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { UserContext } from '@providers/userContextProvider';
import { phoneValidator } from '@utils/common';
import { evaluateTriggerRules } from '@utils/evaluateTriggerRules';
import dayjs from 'dayjs';
import { Box, Grid } from 'hephaestus/web';
import type { CountryCode } from 'libphonenumber-js';
import { kebabCase, uniq } from 'lodash-es';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AmountField } from '../amountField/amountField';
import { BankAccountSelectField } from '../bankAccountSelectField/bankAccountSelect';
import { NumberField } from '../numberField/numberField';
import { PhoneNumberField } from '../phoneNumberField/phoneNumberField';
import { SelectionTile } from '../selectionTile/selectionTile';

export type CustomRenderProps = {
  customOptions: CustomOption[];
  customData: any;
  update: (newValue: any) => void;
  grid?: boolean;
  focused: string[];
  setFocused: any;
  loading: boolean;
  ignoreCustomFieldTriggerRules?: boolean;
  userId?: string;
  lockedMessage?: string;
  /** Array of invalid custom field keys */
  invalidCustomFields?: string[];
  /** Phone country codes map */
  phoneCountryCodes?: Record<string, CountryCode | undefined>;
  /** Setter for phone country codes */
  setPhoneCountryCodes?: (
    codes: Record<string, CountryCode | undefined>,
  ) => void;
};

export const CustomFieldRenderer = ({
  customOptions,
  customData,
  update,
  focused,
  setFocused,
  loading,
  ignoreCustomFieldTriggerRules,
  userId,
  lockedMessage,
  invalidCustomFields = [],
  phoneCountryCodes = {},
  setPhoneCountryCodes,
}: CustomRenderProps) => {
  const { t } = useTranslation('client');

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {customOptions
        .sort(
          (a: CustomOption, b: CustomOption) =>
            (a?.position || 0) - (b?.position || 0),
        )
        .filter((c: CustomOption) =>
          evaluateTriggerRules(
            c.customField,
            customData,
            ignoreCustomFieldTriggerRules,
            update,
          ),
        )
        .map((customOption: CustomOption) => (
          <>
            {customOption.customField &&
              customOption.customField.type === CustomFieldTypes.INTEGER &&
              [CustomFieldFormats.NUMBER].includes(
                customOption.customField.format,
              ) && (
                <NumberField
                  testId={kebabCase(customOption.customField.key)}
                  label={translateBackend(customOption?.label)}
                  placeholder=''
                  disabled={
                    loading ||
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  locked={
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  infoTooltip={additionalInfo(customOption?.additionalInfo)}
                  onBlur={() =>
                    setFocused([...focused, customOption.customField.key])
                  }
                  error={
                    !customData?.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value &&
                    focused.includes(customOption.customField.key) &&
                    customOption?.required !== FieldRequiredType.NOT_REQUIRED
                  }
                  number={
                    customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value ?? ''
                  }
                  setNumber={(e: any) => {
                    let found = false;
                    if (customData.customFields) {
                      customData.customFields.forEach((element: any) => {
                        if (element.key === customOption.customField.key) {
                          element.value = parseInt(e, 10);
                          found = true;
                        }
                      });
                    }
                    if (found) {
                      update({
                        ...customData,
                        customFields: customData.customFields,
                      });
                    } else {
                      const newCustomFields = [
                        ...customData.customFields,
                        {
                          key: customOption.customField.key,
                          value: parseInt(e, 10),
                          customField: customOption.customField,
                        },
                      ];
                      update({ ...customData, customFields: newCustomFields });
                    }
                  }}
                />
              )}

            {customOption.customField &&
              customOption.customField.type === CustomFieldTypes.DECIMAL &&
              [CustomFieldFormats.NUMBER].includes(
                customOption.customField.format,
              ) && (
                <NumberField
                  testId={kebabCase(customOption.customField.key)}
                  label={translateBackend(customOption?.label)}
                  placeholder=''
                  disabled={
                    loading ||
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  locked={
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  infoTooltip={additionalInfo(customOption?.additionalInfo)}
                  onBlur={() =>
                    setFocused([...focused, customOption.customField.key])
                  }
                  error={
                    !customData?.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value &&
                    focused.includes(customOption.customField.key) &&
                    customOption?.required !== FieldRequiredType.NOT_REQUIRED
                  }
                  number={
                    customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value ?? ''
                  }
                  setNumber={(e: any) => {
                    let found = false;
                    if (customData.customFields) {
                      customData.customFields.forEach((element: any) => {
                        if (element.key === customOption.customField.key) {
                          element.value = parseFloat(e);
                          found = true;
                        }
                      });
                    }
                    if (found) {
                      update({
                        ...customData,
                        customFields: customData.customFields,
                      });
                    } else {
                      const newCustomFields = [
                        ...customData.customFields,
                        {
                          key: customOption.customField.key,
                          value: parseFloat(e),
                          customField: customOption.customField,
                        },
                      ];
                      update({ ...customData, customFields: newCustomFields });
                    }
                  }}
                />
              )}

            {customOption.customField &&
              customOption.customField.type === CustomFieldTypes.INTEGER &&
              [CustomFieldFormats.CURRENCY].includes(
                customOption.customField.format,
              ) && (
                <AmountField
                  testId={kebabCase(customOption.customField.key)}
                  infoTooltip={additionalInfo(customOption?.additionalInfo)}
                  placeholder=''
                  disabled={
                    loading ||
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  locked={
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  label={translateBackend(customOption?.label)}
                  amount={
                    customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value ?? ''
                  }
                  onBlur={() =>
                    setFocused([...focused, customOption.customField.key])
                  }
                  error={
                    !customData?.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value &&
                    focused.includes(customOption.customField.key) &&
                    customOption?.required !== FieldRequiredType.NOT_REQUIRED
                  }
                  setAmount={(e: any) => {
                    let found = false;
                    if (customData.customFields) {
                      customData.customFields.forEach((element: any) => {
                        if (element.key === customOption.customField.key) {
                          element.value = parseInt(e, 10);
                          found = true;
                        }
                      });
                    }
                    if (found) {
                      update({
                        ...customData,
                        customFields: customData.customFields,
                      });
                    } else {
                      const newCustomFields = [
                        ...customData.customFields,
                        {
                          key: customOption.customField.key,
                          value: parseInt(e, 10),
                          customField: customOption.customField,
                        },
                      ];
                      update({ ...customData, customFields: newCustomFields });
                    }
                  }}
                />
              )}

            {customOption.customField &&
              customOption.customField.type === CustomFieldTypes.DATE &&
              [CustomFieldFormats.DATE].includes(
                customOption.customField.format,
              ) && (
                <DateField
                  dataTestId={kebabCase(customOption.customField.key)}
                  onChange={(date: any) => {
                    let found = false;
                    customData.customFields.forEach((element: any) => {
                      if (element.key === customOption.customField.key) {
                        element.value = dayjs(date?.toString()).format(
                          'YYYY-MM-DD',
                        );
                        found = true;
                      }
                    });
                    if (found) {
                      update({
                        ...customData,
                        customFields: customData.customFields,
                      });
                    } else {
                      const newCustomFields = [
                        ...customData.customFields,
                        {
                          key: customOption.customField.key,
                          value: dayjs(date?.toString()).format('YYYY-MM-DD'),
                          customField: customOption.customField,
                        },
                      ];
                      update({ ...customData, customFields: newCustomFields });
                    }
                  }}
                  disabled={
                    loading ||
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  label={translateBackend(customOption?.label)}
                  fullWidth
                  infoTooltip={additionalInfo(customOption?.additionalInfo)}
                  value={
                    customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value ?? {}
                  }
                  onBlur={() =>
                    setFocused([...focused, customOption.customField.key])
                  }
                  error={
                    !customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value &&
                    focused.includes(customOption.customField.key) &&
                    customOption?.required !== FieldRequiredType.NOT_REQUIRED
                  }
                />
              )}

            {customOption.customField &&
              customOption.customField.type === CustomFieldTypes.DATE &&
              [CustomFieldFormats.DATE_TIME].includes(
                customOption.customField.format,
              ) && (
                <DateField
                  dataTestId={kebabCase(customOption.customField.key)}
                  onChange={(date: any) => {
                    let found = false;
                    customData.customFields.forEach((element: any) => {
                      if (element.key === customOption.customField.key) {
                        element.value = dayjs(date?.toString()).format(
                          'YYYY-MM-DD HH:mm:ss a',
                        );
                        found = true;
                      }
                    });
                    if (found) {
                      update({
                        ...customData,
                        customFields: customData.customFields,
                      });
                    } else {
                      const newCustomFields = [
                        ...customData.customFields,
                        {
                          key: customOption.customField.key,
                          value: dayjs(date?.toString()).format(
                            'YYYY-MM-DD HH:mm:ss a',
                          ),
                          customField: customOption.customField,
                        },
                      ];
                      update({ ...customData, customFields: newCustomFields });
                    }
                  }}
                  infoTooltip={additionalInfo(customOption?.additionalInfo)}
                  type={customOption.customField.format}
                  disabled={
                    loading ||
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  label={translateBackend(customOption?.label)}
                  fullWidth
                  value={
                    customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value ?? {}
                  }
                  onBlur={() =>
                    setFocused([...focused, customOption.customField.key])
                  }
                  error={
                    !customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value &&
                    focused.includes(customOption.customField.key) &&
                    customOption?.required !== FieldRequiredType.NOT_REQUIRED
                  }
                />
              )}

            {customOption.customField &&
              customOption.customField.type === CustomFieldTypes.DECIMAL &&
              [CustomFieldFormats.CURRENCY].includes(
                customOption.customField.format,
              ) && (
                <AmountField
                  testId={kebabCase(customOption.customField.key)}
                  label={translateBackend(customOption?.label)}
                  placeholder=''
                  disabled={
                    loading ||
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  locked={
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  infoTooltip={additionalInfo(customOption?.additionalInfo)}
                  onBlur={() =>
                    setFocused([...focused, customOption.customField.key])
                  }
                  error={
                    !customData?.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value &&
                    focused.includes(customOption.customField.key) &&
                    customOption?.required !== FieldRequiredType.NOT_REQUIRED
                  }
                  amount={
                    customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value ?? ''
                  }
                  setAmount={(e: any) => {
                    let found = false;
                    if (customData.customFields) {
                      customData.customFields.forEach((element: any) => {
                        if (element.key === customOption.customField.key) {
                          element.value = parseFloat(e);
                          found = true;
                        }
                      });
                    }
                    if (found) {
                      update({
                        ...customData,
                        customFields: customData.customFields,
                      });
                    } else {
                      const newCustomFields = [
                        ...customData.customFields,
                        {
                          key: customOption.customField.key,
                          value: parseFloat(e),
                          customField: customOption.customField,
                        },
                      ];
                      update({ ...customData, customFields: newCustomFields });
                    }
                  }}
                />
              )}

            {customOption.customField &&
              customOption.customField.type ===
                CustomFieldTypes.CUSTOM_OBJECT &&
              customOption.customField.format ===
                CustomFieldFormats.PHYSICAL_ADDRESS && (
                <Grid size={12}>
                  <AddressField
                    testId={kebabCase(customOption.customField.key)}
                    onChange={(e: any) => {
                      let found = false;
                      customData.customFields.forEach((element: any) => {
                        if (element.key === customOption.customField.key) {
                          element.value = e;
                          found = true;
                        }
                      });
                      if (found) {
                        update({
                          ...customData,
                          customFields: customData.customFields,
                        });
                      } else {
                        const newCustomFields = [
                          ...customData.customFields,
                          {
                            key: customOption.customField.key,
                            value: e,
                            customField: customOption.customField,
                          },
                        ];
                        update({
                          ...customData,
                          customFields: newCustomFields,
                        });
                      }
                    }}
                    disabled={
                      loading ||
                      customOption?.required === FieldRequiredType.NOT_EDITABLE
                    }
                    label={translateBackend(customOption?.label)}
                    fullWidth
                    address={
                      customData.customFields?.find(
                        (i: any) => i.key === customOption.customField.key,
                      )?.value ?? {}
                    }
                    onBlur={() =>
                      setFocused([...focused, customOption.customField.key])
                    }
                    error={
                      !customData.customFields?.find(
                        (i: any) => i.key === customOption.customField.key,
                      )?.value &&
                      focused.includes(customOption.customField.key) &&
                      customOption?.required !== FieldRequiredType.NOT_REQUIRED
                    }
                    infoTooltip={additionalInfo(customOption?.additionalInfo)}
                  />
                </Grid>
              )}

            {customOption.customField &&
              customOption.customField.type ===
                CustomFieldTypes.CUSTOM_OBJECT &&
              customOption.customField.format ===
                CustomFieldFormats.CUSTOM_AGREEMENTS && (
                <AccountAgreements
                  label={translateBackend(customOption?.label)}
                  value={
                    customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value ?? {}
                  }
                />
              )}

            {customOption.customField &&
              customOption.customField.type ===
                CustomFieldTypes.CUSTOM_OBJECT &&
              customOption.customField.format ===
                CustomFieldFormats.BANK_ACCOUNT && (
                <BankAccountSelectField
                  bankAccount={
                    customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value
                  }
                  onBlur={() =>
                    setFocused([...focused, customOption.customField.key])
                  }
                  error={
                    !customData?.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value &&
                    focused.includes(customOption.customField.key) &&
                    customOption?.required !== FieldRequiredType.NOT_REQUIRED
                  }
                  setBankAccount={(account) => {
                    let found = false;
                    customData.customFields.forEach((element: any) => {
                      if (element.key === customOption.customField.key) {
                        element.value = account;
                        found = true;
                      }
                    });
                    if (found) {
                      update({
                        ...customData,
                        customFields: customData.customFields,
                      });
                    } else {
                      const newCustomFields = [
                        ...customData.customFields,
                        {
                          key: customOption.customField.key,
                          value: account,
                          customField: customOption.customField,
                        },
                      ];
                      update({ ...customData, customFields: newCustomFields });
                    }
                  }}
                  userId={userId ?? ''}
                />
              )}

            {customOption.customField &&
              customOption.customField.type === CustomFieldTypes.TEXT &&
              [
                CustomFieldFormats.SIMPLE_TEXT,
                CustomFieldFormats.EMAIL,
              ].includes(customOption.customField.format) && (
                <TextField
                  testId={kebabCase(customOption.customField.key)}
                  key={customOption.customField.key}
                  onChange={(e: any) => {
                    let found = false;
                    customData.customFields.forEach((element: any) => {
                      if (element.key === customOption.customField.key) {
                        element.value = e.target.value;
                        found = true;
                      }
                    });
                    if (found) {
                      update({
                        ...customData,
                        customFields: customData.customFields,
                      });
                    } else {
                      const newCustomFields = [
                        ...customData.customFields,
                        {
                          key: customOption.customField.key,
                          value: e.target.value,
                          customField: customOption.customField,
                        },
                      ];
                      update({ ...customData, customFields: newCustomFields });
                    }
                  }}
                  disabled={
                    loading ||
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  label={translateBackend(customOption?.label)}
                  fullWidth
                  infoTooltip={additionalInfo(customOption?.additionalInfo)}
                  value={
                    customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value ?? ''
                  }
                  onBlur={() =>
                    setFocused([...focused, customOption.customField.key])
                  }
                  error={
                    !customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value &&
                    focused.includes(customOption.customField.key) &&
                    customOption?.required !== FieldRequiredType.NOT_REQUIRED
                  }
                  locked={
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  lockMessage={
                    lockedMessage ?? t('pageConfiguration:notEditableMessage')
                  }
                />
              )}

            {customOption.customField &&
              customOption.customField.type === CustomFieldTypes.TEXT &&
              [CustomFieldFormats.PHONE].includes(
                customOption.customField.format,
              ) && (
                <PhoneNumberField
                  testId={kebabCase(customOption.customField.key)}
                  key={customOption.customField.key}
                  onChange={(e: any) => {
                    const fieldKey = customOption.customField.key;
                    const phoneValue = e.value;
                    const countryCode = e.countryCode as
                      | CountryCode
                      | undefined;

                    if (setPhoneCountryCodes) {
                      setPhoneCountryCodes({
                        ...phoneCountryCodes,
                        [fieldKey]: countryCode,
                      });
                    }
                    let found = false;
                    customData.customFields.forEach((element: any) => {
                      if (element.key === fieldKey) {
                        element.value = phoneValue;
                        found = true;
                      }
                    });
                    if (found) {
                      update({
                        ...customData,
                        customFields: customData.customFields,
                      });
                    } else {
                      const newCustomFields = [
                        ...customData.customFields,
                        {
                          key: fieldKey,
                          value: phoneValue,
                          customField: customOption.customField,
                        },
                      ];
                      update({ ...customData, customFields: newCustomFields });
                    }
                  }}
                  disabled={
                    loading ||
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  label={translateBackend(customOption?.label)}
                  infoTooltip={additionalInfo(customOption?.additionalInfo)}
                  value={
                    customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value ?? ''
                  }
                  error={
                    (!customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value &&
                      focused.includes(customOption.customField.key) &&
                      customOption?.required !==
                        FieldRequiredType.NOT_REQUIRED) ||
                    invalidCustomFields.includes(customOption.customField.key)
                  }
                  errorText={
                    invalidCustomFields.includes(customOption.customField.key)
                      ? t('components:fieldErrors.phone')
                      : undefined
                  }
                  locked={
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  lockMessage={
                    lockedMessage ?? t('pageConfiguration:notEditableMessage')
                  }
                />
              )}

            {customOption.customField &&
              customOption.customField.type === CustomFieldTypes.TEXT &&
              customOption.customField.format ===
                CustomFieldFormats.TEXT_AREA && (
                <TextField
                  testId={kebabCase(customOption.customField.key)}
                  multiline={true}
                  key={customOption.customField.key}
                  rows={3}
                  infoTooltip={additionalInfo(customOption?.additionalInfo)}
                  onChange={(e: any) => {
                    let found = false;
                    customData.customFields.forEach((element: any) => {
                      if (element.key === customOption.customField.key) {
                        element.value = e.target.value;
                        found = true;
                      }
                    });
                    if (found) {
                      update({
                        ...customData,
                        customFields: customData.customFields,
                      });
                    } else {
                      const newCustomFields = [
                        ...customData.customFields,
                        {
                          key: customOption.customField.key,
                          value: e.target.value,
                          customField: customOption.customField,
                        },
                      ];
                      update({ ...customData, customFields: newCustomFields });
                    }
                  }}
                  disabled={
                    loading ||
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  label={translateBackend(customOption?.label)}
                  fullWidth
                  value={
                    customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value ?? ''
                  }
                  onBlur={() =>
                    setFocused([...focused, customOption.customField.key])
                  }
                  error={
                    !customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value &&
                    focused.includes(customOption.customField.key) &&
                    customOption?.required !== FieldRequiredType.NOT_REQUIRED
                  }
                  locked={
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  lockMessage={
                    lockedMessage ?? t('pageConfiguration:notEditableMessage')
                  }
                />
              )}

            {customOption.customField &&
              customOption.customField.type === CustomFieldTypes.BOOLEAN &&
              customOption.customField.format ===
                CustomFieldFormats.TOGGLE_SWITCH && (
                <FormControlLabel
                  control={
                    <SwitchField
                      data-testid={kebabCase(customOption.customField.key)}
                      size='small'
                      key={customOption.customField.key}
                      sx={{ paddingLeft: '12px' }}
                      checked={
                        customData.customFields?.find(
                          (i: any) => i.key === customOption.customField.key,
                        )?.value || false
                      }
                      disabled={
                        loading ||
                        customOption?.required ===
                          FieldRequiredType.NOT_EDITABLE
                      }
                      infoTooltip={additionalInfo(customOption?.additionalInfo)}
                      onChange={(e: any) => {
                        let found = false;
                        customData.customFields?.forEach((element: any) => {
                          if (element.key === customOption.customField.key) {
                            element.value = e;
                            found = true;
                          }
                        });
                        if (found) {
                          update({
                            ...customData,
                            customFields: customData.customFields,
                          });
                        } else {
                          const newCustomFields = [
                            ...customData.customFields,
                            {
                              key: customOption.customField.key,
                              value: e,
                              customField: customOption.customField,
                            },
                          ];
                          update({
                            ...customData,
                            customFields: newCustomFields,
                          });
                        }
                      }}
                      label={translateBackend(customOption?.label)}
                    />
                  }
                  label=''
                />
              )}

            {customOption.customField &&
              customOption.customField.type === CustomFieldTypes.BOOLEAN &&
              customOption.customField.format ===
                CustomFieldFormats.CHECKBOX && (
                <FormControlLabel
                  control={
                    <CheckboxField
                      data-testid={kebabCase(customOption.customField.key)}
                      size='small'
                      label={translateBackend(customOption?.label)}
                      infoTooltip={additionalInfo(customOption?.additionalInfo)}
                      key={customOption.customField.key}
                      customStyle={{ paddingLeft: '12px' }}
                      disabled={
                        loading ||
                        customOption?.required ===
                          FieldRequiredType.NOT_EDITABLE
                      }
                      checked={
                        customData?.customFields?.find(
                          (i: any) => i.key === customOption.customField.key,
                        )?.value || false
                      }
                      onChange={(e: any) => {
                        let found = false;
                        customData.customFields?.forEach((element: any) => {
                          if (element.key === customOption.customField.key) {
                            element.value = e;
                            found = true;
                          }
                        });
                        if (found) {
                          update({
                            ...customData,
                            customFields: customData.customFields,
                          });
                        } else {
                          const newCustomFields = [
                            ...customData.customFields,
                            {
                              key: customOption.customField.key,
                              value: e,
                              customField: customOption.customField,
                            },
                          ];
                          update({
                            ...customData,
                            customFields: newCustomFields,
                          });
                        }
                      }}
                    />
                  }
                  label={''}
                />
              )}

            {customOption.customField &&
              customOption.customField.type ===
                CustomFieldTypes.SINGLE_SELECT &&
              [CustomFieldFormats.SEARCH_LIST].includes(
                customOption.customField.format,
              ) && (
                <Autocomplete
                  testId={kebabCase(customOption.customField.key)}
                  label={translateBackend(customOption?.label)}
                  disabled={
                    loading ||
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  locked={
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  lockMessage={
                    lockedMessage ?? t('pageConfiguration:notEditableMessage')
                  }
                  infoTooltip={additionalInfo(customOption?.additionalInfo)}
                  options={
                    customOption?.customField?.selectOptions?.map((x: any) => ({
                      key: x.value,
                      label: translateBackend(x.displayText),
                    })) || []
                  }
                  size='small'
                  value={
                    customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value || ''
                  }
                  isOptionEqualToValue={(option: any, value: any) =>
                    option.key === value.key
                  }
                  fullWidth
                  getOptionLabel={(option) => {
                    if (option && option.key) {
                      return option.label;
                    }
                    const derivedLabel =
                      customOption?.customField?.selectOptions?.find(
                        (item: any) => item.value === option,
                      )?.displayText || '';
                    return translateBackend(derivedLabel);
                  }}
                  renderOption={(props: any, option: any) => (
                    <li
                      {...props}
                      data-testid={`${kebabCase(customOption.customField.key)}-option`}
                    >
                      {option.label}
                    </li>
                  )}
                  onBlur={() =>
                    setFocused([...focused, customOption.customField.key])
                  }
                  error={
                    !customData?.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value &&
                    focused.includes(customOption.customField.key) &&
                    customOption?.required !== FieldRequiredType.NOT_REQUIRED
                  }
                  onChange={(_e: React.SyntheticEvent, value: any) => {
                    let found = false;
                    if (customData?.customFields) {
                      customData?.customFields?.forEach((element: any) => {
                        if (
                          element?.key === customOption?.customField?.key &&
                          value?.key
                        ) {
                          element.value = value?.key;
                          found = true;
                        }
                      });
                    }
                    if (found) {
                      update({
                        ...customData,
                        customFields: customData.customFields,
                      });
                    } else {
                      const newCustomFields = [
                        ...customData.customFields,
                        {
                          key: customOption.customField.key,
                          value: value ? value.key : '',
                          customField: customOption.customField,
                        },
                      ];
                      update({ ...customData, customFields: newCustomFields });
                    }
                  }}
                />
              )}

            {customOption.customField &&
              customOption.customField.type ===
                CustomFieldTypes.SINGLE_SELECT &&
              [CustomFieldFormats.DROPDOWN_LIST].includes(
                customOption.customField.format,
              ) && (
                <SelectField
                  testId={kebabCase(customOption.customField.key)}
                  key={customOption.customField.key}
                  infoTooltip={additionalInfo(customOption?.additionalInfo)}
                  disabled={
                    loading ||
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  locked={
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  lockMessage={
                    lockedMessage ?? t('pageConfiguration:notEditableMessage')
                  }
                  fullWidth
                  value={
                    customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value || ''
                  }
                  label={translateBackend(customOption?.label)}
                  onChange={(e: any) => {
                    let found = false;
                    customData?.customFields?.forEach((element: any) => {
                      if (element.key === customOption.customField.key) {
                        element.value = e.target.value;
                        found = true;
                      }
                    });
                    if (found) {
                      update({
                        ...customData,
                        customFields: customData.customFields,
                      });
                    } else {
                      const newCustomFields = [
                        ...customData.customFields,
                        {
                          key: customOption.customField.key,
                          value: e.target.value,
                          customField: customOption.customField,
                        },
                      ];
                      update({ ...customData, customFields: newCustomFields });
                    }
                  }}
                  onBlur={() =>
                    setFocused([...focused, customOption.customField.key])
                  }
                  error={
                    !customData?.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value &&
                    focused.includes(customOption.customField.key) &&
                    customOption?.required !== FieldRequiredType.NOT_REQUIRED
                  }
                >
                  {customOption?.customField?.selectOptions?.map(
                    (optionItem: {
                      displayText?: TranslatedString;
                      value: any;
                    }) => (
                      <MenuItem
                        key={optionItem.value}
                        value={optionItem.value}
                        data-testid={kebabCase(optionItem.value)}
                      >
                        {translateBackend(optionItem.displayText)}
                      </MenuItem>
                    ),
                  )}
                </SelectField>
              )}

            {customOption.customField &&
              customOption.customField.type ===
                CustomFieldTypes.SINGLE_SELECT &&
              [CustomFieldFormats.RADIO_BUTTON].includes(
                customOption.customField.format,
              ) &&
              customOption?.customField?.selectOptions?.length !== undefined &&
              customOption?.customField.selectOptions.length <= 2 && (
                <RadioGroup
                  testId={kebabCase(customOption.customField.key)}
                  key={customOption.customField.key}
                  label={translateBackend(customOption?.label)}
                  infoTooltip={additionalInfo(customOption?.additionalInfo)}
                  error={
                    !customData?.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value &&
                    focused.includes(customOption.customField.key) &&
                    customOption?.required !== FieldRequiredType.NOT_REQUIRED
                  }
                  value={
                    customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value || ''
                  }
                  fullWidth
                  onChange={(e: any) => {
                    let found = false;
                    customData?.customFields?.forEach((element: any) => {
                      if (element.key === customOption.customField.key) {
                        element.value = e.target.value;
                        found = true;
                      }
                    });
                    if (found) {
                      update({
                        ...customData,
                        customFields: customData.customFields,
                      });
                    } else {
                      const newCustomFields = [
                        ...customData.customFields,
                        {
                          key: customOption.customField.key,
                          value: e.target.value,
                          customField: customOption.customField,
                        },
                      ];
                      update({ ...customData, customFields: newCustomFields });
                    }
                  }}
                >
                  {customOption?.customField?.selectOptions?.map(
                    (optionItem: {
                      displayText?: TranslatedString;
                      value: any;
                    }) => (
                      <Radio
                        value={optionItem.value}
                        testId={kebabCase(optionItem.value)}
                        label={translateBackend(optionItem?.displayText)}
                        disabled={
                          loading ||
                          customOption?.required ===
                            FieldRequiredType.NOT_EDITABLE
                        }
                      />
                    ),
                  )}
                </RadioGroup>
              )}

            {customOption.customField &&
              customOption.customField.type ===
                CustomFieldTypes.SINGLE_SELECT &&
              [CustomFieldFormats.RADIO_BUTTON].includes(
                customOption.customField.format,
              ) &&
              customOption?.customField?.selectOptions?.length !== undefined &&
              customOption?.customField.selectOptions.length > 2 && (
                <SelectionTile
                  sx={
                    Array.isArray(customOptions) &&
                    customOptions[customOptions.length - 1] !== customOption
                      ? { paddingBottom: '32px' }
                      : undefined
                  }
                  testId={kebabCase(customOption.customField.key)}
                  infoTooltip={additionalInfo(customOption?.additionalInfo)}
                  label={translateBackend(customOption?.label)}
                  disabled={
                    loading ||
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  error={
                    !customData?.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value &&
                    focused.includes(customOption.customField.key) &&
                    customOption?.required !== FieldRequiredType.NOT_REQUIRED
                  }
                  value={
                    customData.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value || ''
                  }
                  options={customOption?.customField?.selectOptions.map(
                    (optionItem) => ({
                      label: translateBackend(optionItem?.displayText),
                      value: optionItem?.value,
                    }),
                  )}
                  onChange={(e: any) => {
                    let found = false;
                    customData?.customFields?.forEach((element: any) => {
                      if (element.key === customOption.customField.key) {
                        element.value = e.target.value;
                        found = true;
                      }
                    });
                    if (found) {
                      update({
                        ...customData,
                        customFields: customData.customFields,
                      });
                    } else {
                      const newCustomFields = [
                        ...customData.customFields,
                        {
                          key: customOption.customField.key,
                          value: e.target.value,
                          customField: customOption.customField,
                        },
                      ];
                      update({ ...customData, customFields: newCustomFields });
                    }
                  }}
                />
              )}

            {customOption.customField &&
              customOption.customField.type ===
                CustomFieldTypes.MULTIPLE_SELECT &&
              [CustomFieldFormats.DROPDOWN_LIST].includes(
                customOption.customField.format,
              ) && (
                <>
                  <SelectField
                    testId={kebabCase(customOption.customField.key)}
                    key={customOption.customField.key}
                    disabled={
                      loading ||
                      customOption?.required === FieldRequiredType.NOT_EDITABLE
                    }
                    locked={
                      customOption?.required === FieldRequiredType.NOT_EDITABLE
                    }
                    lockMessage={
                      lockedMessage ?? t('pageConfiguration:notEditableMessage')
                    }
                    infoTooltip={additionalInfo(customOption?.additionalInfo)}
                    fullWidth
                    multiple
                    value={
                      customData.customFields?.find(
                        (i: any) => i.key === customOption.customField.key,
                      )?.value || []
                    }
                    label={translateBackend(customOption?.label)}
                    onChange={(e: any) => {
                      let found = false;
                      customData?.customFields?.forEach((element: any) => {
                        if (element.key === customOption.customField.key) {
                          element.value = e.target.value;
                          found = true;
                        }
                      });
                      if (found) {
                        update({
                          ...customData,
                          customFields: customData.customFields,
                        });
                      } else {
                        const newCustomFields = [
                          ...customData.customFields,
                          {
                            key: customOption.customField.key,
                            value: e.target.value,
                            customField: customOption.customField,
                          },
                        ];
                        update({
                          ...customData,
                          customFields: newCustomFields,
                        });
                      }
                    }}
                    renderValue={(selected: any) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value: any) => {
                          const derivedLabel =
                            customOption?.customField?.selectOptions?.find(
                              (item: any) => item.value === value,
                            )?.displayText || '';
                          return (
                            <Chip
                              size='small'
                              key={value}
                              label={translateBackend(derivedLabel)}
                            />
                          );
                        })}
                      </Box>
                    )}
                    onBlur={() =>
                      setFocused([...focused, customOption.customField.key])
                    }
                    error={
                      customData?.customFields?.find(
                        (i: any) => i.key === customOption.customField.key,
                      )?.value.length === 0 &&
                      focused.includes(customOption.customField.key) &&
                      customOption?.required !== FieldRequiredType.NOT_REQUIRED
                    }
                  >
                    {customOption?.customField?.selectOptions?.map(
                      (optionItem: {
                        displayText?: TranslatedString;
                        value: any;
                      }) => (
                        <MenuItem
                          key={optionItem.value}
                          value={optionItem.value}
                          data-testid={kebabCase(optionItem.value)}
                        >
                          {translateBackend(optionItem.displayText)}
                        </MenuItem>
                      ),
                    )}
                  </SelectField>
                </>
              )}

            {customOption.customField &&
              customOption.customField.type ===
                CustomFieldTypes.MULTIPLE_SELECT &&
              [CustomFieldFormats.CHECKBOX].includes(
                customOption.customField.format,
              ) && (
                <>
                  <SelectField
                    testId={kebabCase(customOption.customField.key)}
                    key={customOption.customField.key}
                    disabled={
                      loading ||
                      customOption?.required === FieldRequiredType.NOT_EDITABLE
                    }
                    locked={
                      customOption?.required === FieldRequiredType.NOT_EDITABLE
                    }
                    lockMessage={
                      lockedMessage ?? t('pageConfiguration:notEditableMessage')
                    }
                    infoTooltip={additionalInfo(customOption?.additionalInfo)}
                    fullWidth
                    multiple
                    value={
                      customData.customFields?.find(
                        (i: any) => i.key === customOption.customField.key,
                      )?.value || []
                    }
                    label={translateBackend(customOption?.label)}
                    onChange={(e: any) => {
                      let found = false;
                      customData?.customFields?.forEach((element: any) => {
                        if (element.key === customOption.customField.key) {
                          element.value = e.target.value;
                          found = true;
                        }
                      });
                      if (found) {
                        update({
                          ...customData,
                          customFields: customData.customFields,
                        });
                      } else {
                        const newCustomFields = [
                          ...customData.customFields,
                          {
                            key: customOption.customField.key,
                            value: e.target.value,
                            customField: customOption.customField,
                          },
                        ];
                        update({
                          ...customData,
                          customFields: newCustomFields,
                        });
                      }
                    }}
                    renderValue={(selected: any) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value: any) => {
                          const derivedLabel =
                            customOption?.customField?.selectOptions?.find(
                              (item: any) => item.value === value,
                            )?.displayText || '';
                          return (
                            <Chip
                              size='small'
                              key={value}
                              label={translateBackend(derivedLabel)}
                            />
                          );
                        })}
                      </Box>
                    )}
                    onBlur={() =>
                      setFocused([...focused, customOption.customField.key])
                    }
                    error={
                      customData?.customFields?.find(
                        (i: any) => i.key === customOption.customField.key,
                      )?.value.length === 0 &&
                      focused.includes(customOption.customField.key) &&
                      customOption?.required !== FieldRequiredType.NOT_REQUIRED
                    }
                  >
                    {customOption?.customField?.selectOptions?.map(
                      (optionItem: {
                        displayText?: TranslatedString;
                        value: any;
                      }) => (
                        <MenuItem
                          key={optionItem.value}
                          value={optionItem.value}
                          data-testid={kebabCase(optionItem.value)}
                        >
                          <CheckboxField
                            customStyle={{ paddingRight: '5px' }}
                            size='small'
                            checked={
                              customData?.customField?.selectOptions?.find(
                                (item: any) => item.value === optionItem.value,
                              ) > -1
                            }
                          />
                          <ListItemText
                            primary={translateBackend(optionItem.displayText)}
                          />
                        </MenuItem>
                      ),
                    )}
                  </SelectField>
                </>
              )}

            {customOption.customField &&
              customOption.customField.type ===
                CustomFieldTypes.MULTIPLE_SELECT &&
              [CustomFieldFormats.SEARCH_LIST].includes(
                customOption.customField.format,
              ) && (
                <Autocomplete
                  testId={kebabCase(customOption.customField.key)}
                  label={translateBackend(customOption?.label)}
                  disabled={
                    loading ||
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  locked={
                    customOption?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  lockMessage={
                    lockedMessage ?? t('pageConfiguration:notEditableMessage')
                  }
                  size='small'
                  infoTooltip={additionalInfo(customOption?.additionalInfo)}
                  error={
                    customData?.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value.length === 0 &&
                    focused.includes(customOption.customField.key) &&
                    customOption?.required !== FieldRequiredType.NOT_REQUIRED
                  }
                  onBlur={() =>
                    setFocused([...focused, customOption.customField.key])
                  }
                  options={
                    customOption?.customField?.selectOptions?.map((x: any) => ({
                      key: x.value,
                      label: translateBackend(x.displayText),
                    })) || []
                  }
                  value={
                    customData?.customFields?.find(
                      (i: any) => i.key === customOption.customField.key,
                    )?.value || []
                  }
                  isOptionEqualToValue={(option: any, value: any) =>
                    option.key === value.key
                  }
                  multiple
                  fullWidth
                  getOptionLabel={(option) => {
                    if (option && option.key) {
                      return option.label;
                    }
                    const derivedLabel =
                      customOption?.customField?.selectOptions?.find(
                        (item: any) => item.value === option,
                      )?.displayText || '';
                    return translateBackend(derivedLabel);
                  }}
                  renderOption={(props: any, option: any) => (
                    <li
                      {...props}
                      data-testid={`${kebabCase(customOption.customField.key)}-option`}
                    >
                      {option.label}
                    </li>
                  )}
                  onChange={(_e: React.SyntheticEvent, value: any) => {
                    let found = false;
                    if (customData?.customFields) {
                      customData?.customFields?.forEach((element: any) => {
                        if (element.key === customOption.customField.key) {
                          element.value = uniq(
                            value.map((a: any) => (a?.key ? a.key : a)),
                          );

                          found = true;
                        }
                      });
                    }
                    if (found) {
                      update({
                        ...customData,
                        customFields: customData.customFields,
                      });
                    } else {
                      const newCustomFields = [
                        ...customData.customFields,
                        {
                          key: customOption.customField.key,
                          value: uniq(value),
                          customField: customOption.customField,
                        },
                      ];
                      update({ ...customData, customFields: newCustomFields });
                    }
                  }}
                />
              )}
          </>
        ))}
    </Box>
  );
};
export default CustomFieldRenderer;
